<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  providerConfig: {
    type: Object,
    default: () => ({}),
  },
  phoneNumber: {
    type: String,
    default: '',
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['connected', 'disconnected', 'statusChange']);

const { t } = useI18n();
const store = useStore();

const session = ref({
  session_id: props.providerConfig?.session_id || null,
  status: props.providerConfig?.session_status || 'not_started',
  qr_code: props.providerConfig?.session_qr_code || null,
  phone_number:
    props.providerConfig?.connected_phone_number ||
    (props.phoneNumber?.startsWith('pending:') ? '' : props.phoneNumber),
  account_name: props.providerConfig?.connected_account_name || null,
  last_error: props.providerConfig?.session_error || null,
});

const isLoading = ref(false);
const qrCodeDataUrl = ref('');
let pollTimer = null;

const POLLING_STATUSES = ['connecting', 'qr_ready', 'reconnecting'];

const applySession = nextSession => {
  session.value = {
    session_id: nextSession?.session_id || session.value.session_id,
    status: nextSession?.status || 'not_started',
    qr_code: nextSession?.qr_code || '',
    phone_number: nextSession?.phone_number || '',
    account_name: nextSession?.account_name || '',
    last_error: nextSession?.last_error || '',
  };
};

const syncInboxes = async () => {
  await store.dispatch('inboxes/get');
};

const updateQrCode = async qrCode => {
  if (!qrCode) {
    qrCodeDataUrl.value = '';
    return;
  }

  qrCodeDataUrl.value = qrCode.startsWith('data:image')
    ? qrCode
    : await QRCode.toDataURL(qrCode);
};

const fetchSession = async ({ create = false, silent = false } = {}) => {
  isLoading.value = !silent;

  try {
    const response = create
      ? await whatsappChannel.createPersonalSession({ inboxId: props.inboxId })
      : await whatsappChannel.getPersonalSession(props.inboxId);

    const previousStatus = session.value.status;
    applySession(response.data.session);

    if (previousStatus !== session.value.status) {
      const nextStatus = session.value.status;

      if (['connected', 'disconnected'].includes(nextStatus)) {
        await syncInboxes();
      }

      emit('statusChange', session.value);

      if (nextStatus === 'connected') {
        useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.CONNECT_SUCCESS'));
        emit('connected', session.value);
      } else if (nextStatus === 'disconnected') {
        emit('disconnected', session.value);
      }
    }
  } catch (error) {
    if (!silent) {
      useAlert(
        error.message ||
          t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.LOAD_ERROR')
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const disconnectSession = async () => {
  isLoading.value = true;

  try {
    const response = await whatsappChannel.deletePersonalSession({
      inboxId: props.inboxId,
    });
    applySession(response.data.session);
    await syncInboxes();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.DISCONNECT_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const statusLabel = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.STATUS.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusDescription = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.STATUS_HELP.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusClass = computed(() => {
  const variants = {
    connected: 'bg-n-teal-3 text-n-teal-11',
    qr_ready: 'bg-n-amber-3 text-n-amber-11',
    connecting: 'bg-n-blue-3 text-n-blue-11',
    reconnecting: 'bg-n-blue-3 text-n-blue-11',
    disconnected: 'bg-n-slate-3 text-n-slate-11',
    error: 'bg-n-ruby-3 text-n-ruby-11',
    not_started: 'bg-n-slate-3 text-n-slate-11',
  };

  return variants[session.value.status] || variants.not_started;
});

const shouldPoll = computed(() => {
  return POLLING_STATUSES.includes(session.value.status);
});

const primaryActionLabel = computed(() => {
  if (session.value.status === 'connected') {
    return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.REFRESH_BUTTON');
  }

  if (session.value.status === 'qr_ready') {
    return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.REFRESH_QR_BUTTON');
  }

  return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.CONNECT_BUTTON');
});

const runPrimaryAction = () => {
  if (session.value.status === 'connected') {
    fetchSession();
    return;
  }

  fetchSession({ create: true });
};

const showDisconnectButton = computed(() => {
  return ['connected', 'connecting', 'qr_ready', 'reconnecting'].includes(
    session.value.status
  );
});

const schedulePoll = () => {
  clearTimeout(pollTimer);
  if (!shouldPoll.value) return;

  const delay = session.value.status === 'qr_ready' ? 2000 : 3000;
  pollTimer = window.setTimeout(() => {
    fetchSession({ silent: true });
  }, delay);
};

watch(
  () => props.providerConfig,
  providerConfig => {
    applySession({
      session_id: providerConfig?.session_id,
      status: providerConfig?.session_status,
      qr_code: providerConfig?.session_qr_code,
      phone_number: providerConfig?.connected_phone_number,
      account_name: providerConfig?.connected_account_name,
      last_error: providerConfig?.session_error,
    });
  },
  { deep: true }
);

watch(
  () => session.value.qr_code,
  qrCode => {
    updateQrCode(qrCode);
  },
  { immediate: true }
);

watch(
  () => session.value.status,
  () => {
    schedulePoll();
  },
  { immediate: true }
);

onMounted(() => {
  fetchSession({ silent: true });
});

onUnmounted(() => {
  clearTimeout(pollTimer);
});
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-alpha-2 p-5">
    <div
      class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0 flex-1">
        <div v-if="showTitle">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.SUBHEADER') }}
          </p>
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-2">
          <span
            class="rounded-full px-2.5 py-1 text-xs font-medium"
            :class="statusClass"
          >
            {{ statusLabel }}
          </span>
          <span
            v-if="session.session_id"
            class="text-xs font-medium text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.SESSION_ID') }}:
            {{ session.session_id }}
          </span>
        </div>

        <p class="mt-3 text-sm text-n-slate-11">
          {{ statusDescription }}
        </p>

        <dl class="mt-4 grid gap-3 text-sm text-n-slate-11">
          <div v-if="session.account_name" class="flex flex-col gap-1">
            <dt class="font-medium text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.CONNECTED_ACCOUNT'
                )
              }}
            </dt>
            <dd>{{ session.account_name }}</dd>
          </div>

          <div v-if="session.phone_number" class="flex flex-col gap-1">
            <dt class="font-medium text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.CONNECTED_NUMBER'
                )
              }}
            </dt>
            <dd>{{ session.phone_number }}</dd>
          </div>

          <div v-if="session.last_error" class="flex flex-col gap-1">
            <dt class="font-medium text-n-ruby-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.LAST_ERROR') }}
            </dt>
            <dd class="text-n-ruby-11">{{ session.last_error }}</dd>
          </div>
        </dl>
      </div>

      <div
        v-if="qrCodeDataUrl"
        class="flex flex-col items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 p-4"
      >
        <img
          :src="qrCodeDataUrl"
          alt="WhatsApp Personal QR"
          class="size-56 rounded-xl"
        />
        <p class="max-w-56 text-center text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.QR_HELP') }}
        </p>
      </div>
    </div>

    <div class="mt-5 flex flex-wrap gap-2">
      <Button
        :label="primaryActionLabel"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="runPrimaryAction"
      />
      <Button
        v-if="showDisconnectButton"
        :label="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PERSONAL.DISCONNECT_BUTTON')
        "
        color="slate"
        variant="faded"
        :disabled="isLoading"
        @click="disconnectSession"
      />
    </div>
  </div>
</template>
